import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Award, ShieldCheck, XCircle, Loader2, Download, GraduationCap } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Public, login-free verification page for HR Training certificates (SC-XXXXXXXX). */
export default function TrainingCertificatePage() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    fetch(`${BACKEND}/api/training/certificates/verify/${encodeURIComponent(certId || '')}`)
      .then(r => r.json())
      .then(data => setState({ loading: false, data }))
      .catch(() => setState({ loading: false, data: { valid: false } }));
  }, [certId]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6" data-testid="training-cert-loading">
        <Loader2 className="w-10 h-10 text-indigo-400 animate-spin" />
      </div>
    );
  }

  const d = state.data || {};
  if (!d.valid) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center shadow-2xl" data-testid="training-cert-invalid">
          <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Certificate not found</h1>
          <p className="text-slate-600 mb-6">No valid training certificate matches <code className="bg-slate-100 px-1.5 py-0.5 rounded">{certId}</code>.</p>
          <Link to="/" className="inline-flex px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold" data-testid="training-cert-home">Go to ScensobConnect</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top,_#4f46e5_0%,_transparent_60%)]" />
      <div className="relative max-w-2xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden" data-testid="training-cert-valid">
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-8 text-white">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 mb-3"><GraduationCap className="w-4 h-4" /> ScensobConnect · HR Training</div>
          <h1 className="text-3xl sm:text-4xl font-bold">Certificate of Completion</h1>
        </div>
        <div className="p-8 space-y-6">
          <div>
            <p className="text-sm text-slate-500">This certifies that</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="training-cert-worker">{d.worker_name}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">has successfully completed</p>
            <p className="text-2xl font-bold text-indigo-700 mt-1" data-testid="training-cert-course">{d.course_title}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="rounded-xl bg-slate-50 p-3"><p className="text-slate-500 text-xs">Issued</p><p className="font-semibold text-slate-900">{d.issued_at ? new Date(d.issued_at).toLocaleDateString(undefined, { dateStyle: 'long' }) : '—'}</p></div>
            <div className="rounded-xl bg-slate-50 p-3"><p className="text-slate-500 text-xs">Score</p><p className="font-semibold text-slate-900">{d.score != null ? `${d.score}%` : 'All sections completed'}</p></div>
            <div className="rounded-xl bg-slate-50 p-3"><p className="text-slate-500 text-xs">Issued by</p><p className="font-semibold text-slate-900">{d.issued_by || 'HR Team'}</p></div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-slate-100">
            <div className="inline-flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 text-sm font-semibold" data-testid="training-cert-seal">
              <ShieldCheck className="w-4 h-4" /> Verified · {d.certificate_id}
            </div>
            <a href={`${BACKEND}/api/training/certificates/${d.certificate_id}.pdf`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
              data-testid="training-cert-pdf">
              <Download className="w-4 h-4" /> Download PDF
            </a>
          </div>
        </div>
        <div className="px-8 py-3 bg-slate-50 text-[11px] text-slate-500 flex items-center gap-1"><Award className="w-3.5 h-3.5" /> Issued through ScensobConnect HR Training. This page is public and shows only what the certificate itself contains.</div>
      </div>
    </div>
  );
}
